import { CheckCircleFilled, EditOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, App, Button, Checkbox, Col, Divider, Form, Input, InputRef, Modal, Row, Select, Tabs, Tag, theme } from 'antd';
import { isEqualWith, uniq } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NumberInput from 'src/common/components/input/number-input';
import useMediaQuery from 'src/common/hooks/use-media-query';
import { nameof } from 'src/core/utils/object';
import { container } from 'src/inversify.config';
import {
  AccessTokenType,
  ClientItem,
  ClientItemStore,
  defaultClientIds,
  defaultClientItem,
  getGrantTypeLabel,
  getGrantTypesFromKey,
  GrantTypes,
  ProtocolType,
  StandardScopes,
  TokenUsage,
} from 'src/stores/clients-store';
import { ClientUsage } from './client-usage';
const { useToken } = theme;

type TabKey = 'general' | 'uris' | 'secrets';

export const ClientDetailsPage: FC<{ mode: 'new' | 'edit'; onClose: (changed?: boolean) => void; clientItem?: ClientItem }> = ({
  mode,
  onClose,
  clientItem,
}) => {
  const newCLientMode = mode === 'new';
  const { t } = useTranslation();
  const [form] = Form.useForm<ClientItem>();
  const [submittable, setSubmittable] = useState<boolean>(false);
  const values = Form.useWatch([], form);
  const [currentTabKey, setCurrentTabKey] = useState<TabKey>('general');
  const { token } = useToken();
  const [allowedScopeOptions, setAllowedScopeOptions] = useState<{ label: string; value: StandardScopes | string }[]>([
    ...Object.entries(StandardScopes)
      .filter(([key]) => isNaN(parseInt(key)))
      .map(([key, value]) => ({ label: t(key), value })),
    ...(clientItem?.allowedScopes.filter((v) => !Object.values(StandardScopes).includes(v as StandardScopes)).map((v) => ({ label: v, value: v })) ??
      []),
  ]);
  const clientsStore = container.get(ClientItemStore);
  const [loading, setLoading] = useState(false);

  const [newAllowedScope, setNewAllowedScope] = useState('');
  const inputRef = useRef<InputRef>(null);
  const onMobile = useMediaQuery('(max-width: 576px)');

  const [noSecretsWarningVisible, setNoSecretsWarningVisible] = useState(newCLientMode);
  const [noChangesMadeWarning, setNoChangesMadeWarning] = useState(!newCLientMode);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [secretsEnabled, setSecretsEnabled] = useState(clientItem?.requireClientSecret ?? true);
  const { modal } = App.useApp();
  const [editedSecretIds, setEditedSecretIds] = useState<number[]>([]);

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewAllowedScope(event.target.value);
  };

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((validated) => {
        let unchanged = false;
        let validatedValues = validated;
        if (validated.grantTypesKey) validatedValues.allowedGrantTypes = getGrantTypesFromKey(validated.grantTypesKey);
        if (clientItem) {
          unchanged = isEqualWith(validatedValues, clientItem);
        }
        setSecretsEnabled(validated.requireClientSecret);
        setNoChangesMadeWarning(!newCLientMode && unchanged);
        setNoSecretsWarningVisible(validatedValues.requireClientSecret && validatedValues.clientSecrets.length === 0);
        setErrorMessages([]);
        setSubmittable(!unchanged && (!validatedValues.requireClientSecret || validatedValues.clientSecrets.length > 0));
      })
      .catch((e) => {
        const errorData: {
          errorFields: {
            errors: string[];
            name: string[];
          }[];
          values: Partial<ClientItem>;
        } = e;
        const errorMessages = errorData.errorFields.map((v) => v.errors).flat();
        const uniqueErrorMessages = uniq(errorMessages);
        setSecretsEnabled(values?.requireClientSecret ?? false);
        setErrorMessages(uniqueErrorMessages);
        setNoSecretsWarningVisible(values?.requireClientSecret && values?.clientSecrets.length === 0);
        setSubmittable(false);
      });
  }, [form, values]); // eslint-disable-line react-hooks/exhaustive-deps

  const getClientData = async () => {
    let item = await form.validateFields();
    item.allowedGrantTypes = getGrantTypesFromKey(item.grantTypesKey!);
    delete item?.grantTypesKey;
    if (!item.requireClientSecret && newCLientMode) {
      item.clientSecrets = [];
    }
    return item;
  };

  const onNewClient = async () => {
    setLoading(true);
    const item = await getClientData();
    if (item) {
      const response = await clientsStore.create(item);
      if (response?.isSuccess) {
        onClose(true);
      }
    }
    setLoading(false);
  };

  const onEditClient = async () => {
    setLoading(true);
    let item = await getClientData();
    const secretsWithUpdatedDescription =
      clientItem?.clientSecrets.filter(
        (oldSecret) => item.clientSecrets.find((newSecret) => newSecret.id === oldSecret.id)?.description !== oldSecret.description
      ) ?? [];

    const secretsToEdit = item.clientSecrets.filter((v) => v.id !== undefined && editedSecretIds.includes(v.id));
    const secretsWithUpdatedDescriptionIds = secretsWithUpdatedDescription
      .flatMap((v) => (v.id !== undefined ? [v.id] : []))
      .filter((v) => !editedSecretIds.includes(v));

    secretsToEdit.push(
      ...item.clientSecrets
        .filter((v) => v.id !== undefined && secretsWithUpdatedDescriptionIds.includes(v.id))
        .map((v) => ({ id: v.id, description: v.description }))
    );
    const secretsToDelete =
      clientItem?.clientSecrets
        .filter((oldSecret) => !item.clientSecrets.some((newSecret) => newSecret.id === oldSecret.id))
        .flatMap((s) => (s.id !== undefined ? [s.id] : [])) ?? [];
    const secretsToAdd = item.clientSecrets.filter((s) => s.id === undefined);

    const editItem = { ...item, secretsToAdd, secretsToEdit, secretsToDelete, clientSecrets: undefined };

    if (item && clientItem) {
      const response = await clientsStore.save(clientItem?.clientId, editItem);
      if (response?.isSuccess) {
        onClose(true);
      }
    }
    setLoading(false);
  };

  const addAllowedScopeOption = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    setAllowedScopeOptions((prev) => [...prev, { label: newAllowedScope, value: newAllowedScope }]);
    const prev = form.getFieldValue('allowedScopes');
    form.setFieldsValue({ allowedScopes: [...prev, newAllowedScope] });
    setNewAllowedScope('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const tabItems = [
    {
      forceRender: true,
      label: t('General'),
      key: 'general',
      children: (
        <Row gutter={10}>
          <Col md={7} sm={11} xs={24}>
            <Form.Item
              style={{ marginBottom: 12 }}
              required
              name={nameof<ClientItem>('clientId')}
              label={t('ClientID')}
              rules={[
                { required: true, message: t('PleaseEnterAClientId') },
                () => ({
                  validator(_, value: string) {
                    if (value && value.trim().length === 0) return Promise.reject(t('PleaseEnterAClientId'));
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
              style={{ marginBottom: 12 }}
              required
              name={nameof<ClientItem>('clientName')}
              label={t('ClientName')}
              rules={[
                { required: true, message: t('PleaseEnterAClientName') },
                () => ({
                  validator(_, value: string) {
                    if (value && value.trim().length === 0) return Promise.reject(t('PleaseEnterAClientName'));
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item required style={{ marginBottom: 16 }} name={nameof<ClientItem>('protocolType')} label={t('ProtocolType')}>
              <Select options={Object.entries(ProtocolType).map(([key, value]) => ({ label: t(key), value }))} />
            </Form.Item>
            <Form.Item
              style={{ marginBottom: 12 }}
              required
              name={nameof<ClientItem>('grantTypesKey')}
              label={t('AllowedGrantTypes')}
              rules={[{ required: true, message: t('PleaseSelectTheAllowedGrantTypes') }]}
            >
              <Select
                style={{ height: '100%' }}
                className="full-height-select"
                options={Object.entries(GrantTypes).map(([key, value], i) => ({
                  label: (
                    <div key={i} style={{ background: token.colorBgContainerDisabled, padding: 2, display: 'flex', flexWrap: 'wrap' }}>
                      {value.map((v, j) => (
                        <Tag style={{ margin: 4 }} key={j}>
                          {t(getGrantTypeLabel(v))}
                        </Tag>
                      ))}
                    </div>
                  ),
                  value: key,
                }))}
              />
            </Form.Item>
          </Col>

          {!onMobile && (
            <Col span={1}>
              <Divider style={{ height: '100%' }} type="vertical" />
            </Col>
          )}

          <Col md={7} sm={11} xs={24}>
            <Form.Item
              required
              rules={[{ required: true, message: t('PleaseSelectTheAllowedScopes') }]}
              style={{ marginBottom: 12 }}
              name={nameof<ClientItem>('allowedScopes')}
              label={t('AllowedScopes')}
            >
              <Select
                mode="tags"
                options={allowedScopeOptions}
                popupRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <Button
                      style={{ marginRight: 4 }}
                      disabled={newAllowedScope.trim() === ''}
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={addAllowedScopeOption}
                    />
                    <Input
                      placeholder={t('AddNew')}
                      style={{ width: '85%' }}
                      ref={inputRef}
                      value={newAllowedScope}
                      onChange={onNameChange}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </>
                )}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 12 }} name={nameof<ClientItem>('accessTokenLifetime')} label={`${t('AccessTokenLifetime')} (s)`}>
              <NumberInput onlyIntegers />
            </Form.Item>
            <Form.Item
              style={{ marginBottom: 12 }}
              name={nameof<ClientItem>('absoluteRefreshTokenLifetime')}
              label={`${t('AbsoluteRefreshTokenLifetime')} (s)`}
            >
              <NumberInput onlyIntegers />
            </Form.Item>
            <Form.Item style={{ marginBottom: 12 }} name={nameof<ClientItem>('refreshTokenUsage')} label={t('RefreshTokenUsage')}>
              <Select
                options={Object.entries(TokenUsage)
                  .filter(([key]) => isNaN(parseInt(key)))
                  .map(([key, value]) => ({ label: t(key), value }))}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 12 }} name={nameof<ClientItem>('accessTokenType')} label={t('AccessTokenType')}>
              <Select
                options={Object.entries(AccessTokenType)
                  .filter(([key]) => isNaN(parseInt(key)))
                  .map(([key, value]) => ({ label: t(key), value }))}
              />
            </Form.Item>
          </Col>

          {!onMobile && (
            <Col span={1}>
              <Divider style={{ height: '100%' }} type="vertical" />
            </Col>
          )}

          <Col md={7} sm={24} xs={24}>
            <Form.Item valuePropName="checked" style={{ marginBottom: 12 }} name={nameof<ClientItem>('requirePkce')}>
              <Checkbox>{t('RequirePkce')}</Checkbox>
            </Form.Item>
            <Form.Item valuePropName="checked" style={{ marginBottom: 12 }} name={nameof<ClientItem>('requireClientSecret')}>
              <Checkbox>{t('RequireClientSecret')}</Checkbox>
            </Form.Item>
            <Form.Item valuePropName="checked" style={{ marginBottom: 12 }} name={nameof<ClientItem>('allowAccessTokensViaBrowser')}>
              <Checkbox>{t('AllowAccessTokensViaBrowser')}</Checkbox>
            </Form.Item>
            <Form.Item valuePropName="checked" style={{ marginBottom: 12 }} name={nameof<ClientItem>('allowOfflineAccess')}>
              <Checkbox>{t('AllowOfflineAccess')}</Checkbox>
            </Form.Item>
            <Form.Item valuePropName="checked" style={{ marginBottom: 12 }} name={nameof<ClientItem>('alwaysIncludeUserClaimsInIdToken')}>
              <Checkbox>{t('AlwaysIncludeUserClaimsInIdToken')}</Checkbox>
            </Form.Item>
            <Form.Item valuePropName="checked" style={{ marginBottom: 12 }} name={nameof<ClientItem>('requireConsent')}>
              <Checkbox>{t('RequireConsent')}</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      ),
    },
    {
      forceRender: true,
      label: t('RedirectUris'),
      key: 'uris',
      children: (
        <Row>
          <Col md={11} sm={24}>
            <Form.List name={nameof<ClientItem>('redirectUris')}>
              {(fields, { add, remove }) => (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <span>{t('RedirectUris')}</span>
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: '40vh' }}>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row key={key}>
                        <Col span={21}>
                          <Form.Item
                            {...restField}
                            name={name}
                            rules={[
                              { required: true, message: t('RedirectURIMustNotBeEmpty') },
                              () => ({
                                validator(_, value: string) {
                                  if (value && value.trim().length === 0) return Promise.reject(t('RedirectURIMustNotBeEmpty'));
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <Input autoComplete="off" />
                          </Form.Item>
                        </Col>
                        <Col span={1} style={{ marginLeft: 8 }}>
                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Col>
                      </Row>
                    ))}
                  </div>
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      {t('AddRedirectUri')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
          {!onMobile && (
            <Col span={1}>
              <Divider style={{ height: '100%' }} type="vertical" />
            </Col>
          )}
          <Col md={12} sm={24}>
            <Form.List name={nameof<ClientItem>('postLogoutRedirectUris')}>
              {(fields, { add, remove }) => (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <span>{t('PostLogoutRedirectUris')}</span>
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: '40vh' }}>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row key={key}>
                        <Col span={21}>
                          <Form.Item
                            {...restField}
                            name={name}
                            rules={[
                              { required: true, message: t('PostLogoutRedirectURIMustNotBeEmpty') },
                              () => ({
                                validator(_, value: string) {
                                  if (value && value.trim().length === 0) return Promise.reject(t('PostLogoutRedirectURIMustNotBeEmpty'));
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <Input autoComplete="off" />
                          </Form.Item>
                        </Col>
                        <Col span={1} style={{ marginLeft: 8 }}>
                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Col>
                      </Row>
                    ))}
                  </div>
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      {t('AddPostLogoutRedirectUri')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      ),
    },
    {
      forceRender: true,
      disabled: !secretsEnabled,
      label: t('ClientSecrets'),
      key: 'secrets',
      children: (
        <Form.List name={nameof<ClientItem>('clientSecrets')}>
          {(fields, { add, remove }) => (
            <>
              <div style={{ overflowY: 'auto', overflowX: 'clip', maxHeight: '40vh' }}>
                {fields.map(({ key, name, ...restField }) => (
                  <>
                    <Row key={`${key}-content`} gutter={[10, 10]}>
                      <Col md={10} sm={24} xs={24}>
                        <Form.Item
                          {...restField}
                          required
                          style={{ marginBottom: 12 }}
                          label={t('Value')}
                          name={[name, 'value']}
                          rules={[
                            { required: true, message: t('SecretValueMustNotBeEmpty') },
                            () => ({
                              validator(_, value: string) {
                                if (value && value.trim().length === 0) return Promise.reject(t('SecretValueMustNotBeEmpty'));
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Input
                            autoComplete="off"
                            disabled={
                              mode === 'edit' &&
                              form.getFieldValue(['clientSecrets', name, 'id']) !== undefined &&
                              !editedSecretIds.includes(form.getFieldValue(['clientSecrets', name, 'id']))
                            }
                          />
                        </Form.Item>
                        {form.getFieldValue(['clientSecrets', name, 'id']) !== undefined &&
                          !editedSecretIds.includes(form.getFieldValue(['clientSecrets', name, 'id'])) && (
                            <EditOutlined
                              onClick={() => {
                                modal.confirm({
                                  title: t('ConfirmEditSecretValueTitle'),
                                  content: t('ConfirmEditSecretValueMessage').replace(
                                    '{0}',
                                    form.getFieldValue(['clientSecrets', name, 'description'])
                                  ),
                                  onOk: () => {
                                    form.setFieldValue(['clientSecrets', name, 'value'], undefined);
                                    setEditedSecretIds((prev) => [...prev, form.getFieldValue(['clientSecrets', name, 'id'])]);
                                  },
                                });
                              }}
                              style={{
                                position: 'absolute',
                                top: 32,
                                right: 4,
                                color: token.colorPrimary,
                                cursor: 'pointer',
                                padding: 12,
                                height: 14,
                              }}
                            />
                          )}

                        <Form.Item hidden name={[name, 'id']}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col md={11} sm={21} xs={21}>
                        <Form.Item
                          {...restField}
                          required
                          style={{ marginBottom: 12 }}
                          label={t('Description')}
                          name={[name, 'description']}
                          rules={[
                            { required: true, message: t('SecretDescriptionMustNotBeEmpty') },
                            () => ({
                              validator(_, value: string) {
                                if (value && value.trim().length === 0) return Promise.reject(t('SecretDescriptionMustNotBeEmpty'));
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Input.TextArea style={{ height: 80 }} autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={1} style={{ marginLeft: 8 }}>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ position: 'relative', top: '40%' }} />
                      </Col>
                    </Row>
                    {onMobile && <Divider key={`${key}-divider`} />}
                  </>
                ))}
              </div>
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('AddClientSecret')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      ),
    },
  ];

  if (mode === 'edit' && !defaultClientIds.includes(clientItem?.clientId ?? ''))
    tabItems.push({
      forceRender: false,
      label: t('Usage'),
      key: 'client-usage',
      children: <ClientUsage clientId={clientItem?.clientId ?? ''} />,
    });

  return (
    <Modal
      width={1200}
      title={newCLientMode ? t('NewClient') : t('EditClient')}
      open
      onCancel={() => onClose(false)}
      onOk={newCLientMode ? onNewClient : onEditClient}
      okButtonProps={{ disabled: !submittable, loading: loading }}
    >
      {(errorMessages.length > 0 || noSecretsWarningVisible || noChangesMadeWarning) && (
        <Alert
          type="warning"
          message={
            <ul>
              {errorMessages.map((v, i) => (
                <li key={i}>{t(v)}</li>
              ))}
              {noSecretsWarningVisible && <li>{t('NoSecretsWarning')}</li>}
              {noChangesMadeWarning && <li>{t('NoChangesMadeWarning')}</li>}
            </ul>
          }
        />
      )}

      {!(errorMessages.length > 0 || noSecretsWarningVisible || noChangesMadeWarning) && (
        <Alert
          type="success"
          message={
            <>
              <CheckCircleFilled style={{ marginRight: 8 }} /> <span>{t('AllDataEnterdIsCorrect')}</span>
            </>
          }
        />
      )}

      <Form layout="vertical" form={form} initialValues={clientItem ?? defaultClientItem}>
        <Tabs className="no-border-tabpane" activeKey={currentTabKey} onTabClick={(key) => setCurrentTabKey(key as TabKey)} items={tabItems} />
      </Form>
      <Divider style={{ width: '100%', marginBottom: 4 }} />
    </Modal>
  );
};
